import { OcrConfig } from './ocr-types'

export interface CtcDecodeResult {
  text: string
  confidence: number
}

export type DecodeMode = 'fast'

export function greedyCtcDecode(
  logits: Float32Array,
  config: OcrConfig,
  idToToken: Record<number, string>
): CtcDecodeResult {
  const { ctcClasses, ctcOffset, unkToken, vocabSize } = config
  const T = Math.floor(logits.length / ctcClasses)

  const bestIds: number[] = []
  const confidences: number[] = []

  for (let t = 0; t < T; t++) {
    const offset = t * ctcClasses
    let maxLogit = -Infinity
    let maxIdx = 0

    for (let c = 0; c < ctcClasses; c++) {
      const val = logits[offset + c] ?? -Infinity
      if (val > maxLogit) {
        maxLogit = val
        maxIdx = c
      }
    }

    let sumExp = 0
    for (let c = 0; c < ctcClasses; c++) {
      const val = logits[offset + c] ?? -Infinity
      sumExp += Math.exp(val - maxLogit)
    }
    const prob = 1.0 / sumExp

    bestIds.push(maxIdx)
    confidences.push(prob)
  }

  const chars: string[] = []
  let prevId = -1
  let totalConf = 0
  let confCount = 0
  let skippedBlank = 0, skippedDup = 0, skippedOOB = 0, skippedUnk = 0, kept = 0

  for (let t = 0; t < T; t++) {
    const id = bestIds[t]
    if (id === undefined) continue
    if (id === prevId) { skippedDup++; continue }
    prevId = id
    if (id < ctcOffset) { skippedBlank++; continue }

    const rawId = id - ctcOffset
    if (rawId >= 0 && rawId < vocabSize) {
      const char = idToToken[rawId] || ''
      if (char && char !== unkToken) {
        chars.push(char)
        totalConf += confidences[t] ?? 0
        confCount++
        kept++
      } else {
        skippedUnk++
      }
    } else {
      skippedOOB++
    }
  }

  const confidence = confCount > 0 ? totalConf / confCount : 0

  if ((window as any).__OCR_DEBUG__ && chars.length === 0) {
    console.group(`[CTC Greedy ⚠️ EMPTY] T=${T} — token filter breakdown`)
    console.log('Blank (id < ctcOffset):', skippedBlank, `/${T} (${((skippedBlank/T)*100).toFixed(0)}%)`)
    console.log('Duplicates (id === prevId):', skippedDup)
    console.log('Out of vocab (rawId >= vocabSize):', skippedOOB)
    console.log('Unknown token (unkToken):', skippedUnk)
    console.log('Kept:', kept)
    console.log('ctcOffset:', ctcOffset, 'vocabSize:', vocabSize)
    console.log('First 20 raw IDs:', bestIds.slice(0, 20).map(id => {
      const raw = (id ?? 0) - ctcOffset
      return `${id}(${idToToken[raw] ?? '?'})`
    }).join(', '))
    console.groupEnd()
  }

  if ((window as any).__OCR_DEBUG__) {
    const blankRatios: number[] = []
    for (let t = 0; t < T; t += Math.max(1, Math.floor(T / 10))) {
      const id = bestIds[t]!
      blankRatios.push(id === config.blankId ? 1 : 0)
    }
    const blankRatio = blankRatios.reduce((a, b) => a + b, 0) / Math.max(1, blankRatios.length)
    console.groupCollapsed(`[CTC Greedy] T=${T} text="${chars.join('')}"`)
    console.log('Top IDs (every 10th):', bestIds.filter((_, i) => i % Math.max(1, Math.floor(T / 10)) === 0))
    console.log('Blank ratio (sampled):', `${(blankRatio * 100).toFixed(1)}%`)
    console.log('Chars:', chars)
    console.log('Confidence:', confidence.toFixed(3))
    console.groupEnd()
  }

  return {
    text: chars.join(''),
    confidence,
  }
}

export function ctcBeamSearch(
  logits: Float32Array,
  config: OcrConfig,
  idToToken: Record<number, string>,
  beamWidth: number = 3
): CtcDecodeResult {
  const { ctcClasses, ctcOffset, blankId, unkToken, vocabSize } = config
  const T = Math.floor(logits.length / ctcClasses)

  const beamMap = new Map<string, { score: number; blankScore: number; tokenIds: number[] }>()
  beamMap.set('', { score: 0, blankScore: 0, tokenIds: [] })

  for (let t = 0; t < T; t++) {
    const offset = t * ctcClasses

    const logProbs = new Float32Array(ctcClasses)
    let maxLogit = -Infinity
    for (let c = 0; c < ctcClasses; c++) {
      const val = logits[offset + c] ?? -Infinity
      if (val > maxLogit) maxLogit = val
      logProbs[c] = val
    }

    let sumExp = 0
    for (let c = 0; c < ctcClasses; c++) {
      const p = Math.exp(logProbs[c]! - maxLogit)
      logProbs[c] = p
      sumExp += p
    }
    for (let c = 0; c < ctcClasses; c++) {
      logProbs[c] = Math.log((logProbs[c] ?? 0) / sumExp)
    }

    const nextMap = new Map<string, { score: number; blankScore: number; tokenIds: number[] }>()

    for (const [prefixKey, beam] of beamMap) {
      const prevTok = beam.tokenIds.length > 0 ? beam.tokenIds[beam.tokenIds.length - 1]! : -1

      const blankProb = logProbs[blankId] ?? -Infinity
      const newBlankKey = prefixKey + '\x00'
      const existing = nextMap.get(newBlankKey)
      if (!existing || beam.score + blankProb > existing.score) {
        nextMap.set(newBlankKey, {
          score: beam.score + blankProb,
          blankScore: beam.score + blankProb,
          tokenIds: beam.tokenIds,
        })
      }

      for (let c = ctcOffset; c < ctcClasses; c++) {
        if (c === blankId) continue
        const prob = logProbs[c] ?? -Infinity
        if (prob < -10) continue

        const rawId = c - ctcOffset
        const token = idToToken[rawId]
        if (!token || token === unkToken) continue

        const newTokenIds = prevTok === c ? beam.tokenIds : [...beam.tokenIds, c]
        const newKey = newTokenIds.join(',')
        const newScore = beam.blankScore + prob

        const existing = nextMap.get(newKey)
        if (!existing || newScore > existing.score) {
          const existingBlank = nextMap.get(newKey + '\x00')
          nextMap.set(newKey, {
            score: newScore,
            blankScore: existingBlank ? Math.max(existingBlank.score, newScore) : newScore,
            tokenIds: newTokenIds,
          })
        }
      }
    }

    beamMap.clear()

    const sorted = Array.from(nextMap.entries())
      .filter(([k]) => !k.endsWith('\x00'))
      .sort((a, b) => b[1].score - a[1].score)
      .slice(0, beamWidth)

    for (const [key, value] of sorted) {
      beamMap.set(key, value)
    }
  }

  if (beamMap.size === 0) {
    return { text: '', confidence: 0 }
  }

  let best: { score: number; tokenIds: number[] } = { score: -Infinity, tokenIds: [] }
  for (const [, beam] of beamMap) {
    if (beam.score > best.score) {
      best = { score: beam.score, tokenIds: beam.tokenIds }
    }
  }

  const chars: string[] = []
  for (const c of best.tokenIds) {
    const rawId = c - ctcOffset
    if (rawId >= 0 && rawId < vocabSize) {
      const char = idToToken[rawId] || ''
      if (char && char !== unkToken) {
        chars.push(char)
      }
    }
  }

  return {
    text: chars.join(''),
    confidence: Math.exp(best.score / Math.max(1, T)),
  }
}

export function flattenLogits(logits: number[][][]): Float32Array {
  const batch = logits
  const T = batch[0]?.length ?? 0
  const C = batch[0]?.[0]?.length ?? 0
  const flat = new Float32Array(T * C)
  for (let t = 0; t < T; t++) {
    for (let c = 0; c < C; c++) {
      flat[t * C + c] = batch[0]?.[t]?.[c] ?? 0
    }
  }
  return flat
}
